import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@mui/material';
import { Stack } from '@mui/system';

import BookingContactForm from '@/features/BookingsServices/types/bookingContactForm.type';

import AddContactFormList from '../../AddContactFormList';

type ContactsDetailsProps = {
  shouldValidate: boolean;
  loading?: boolean;
  proceed: (contacts: BookingContactForm[]) => void;
  setShouldValidate: (value: boolean) => void;
};

const ContactsDetails: FC<ContactsDetailsProps> = ({
  shouldValidate,
  loading,
  proceed,
  setShouldValidate,
}) => {
  const { t } = useTranslation(['bookingsServices', 'common']);

  const [warning, setWarning] = useState<string>('');
  const [contactsForms, setContactsForms] = useState<
    ({ id: string } & BookingContactForm)[]
  >([]);
  const [formErrors, setFormErrors] = useState<
    {
      id: string;
      booking_contact_id: string;
      calendar_ids: string;
    }[]
  >([]);

  const onValidateContacts = useCallback(() => {
    const errors = contactsForms.filter(
      (contact) =>
        !contact.booking_contact_id || contact.calendar_ids.length === 0,
    );

    setFormErrors(
      errors.map((error) => ({
        id: error.id,
        booking_contact_id: !error.booking_contact_id
          ? t('bookingsServices:booking_contact_id_required')
          : '',
        calendar_ids:
          error.calendar_ids.length === 0
            ? t('bookingsServices:calendar_ids_required')
            : '',
      })),
    );

    return errors.length === 0;
  }, [contactsForms, t]);

  const onProceedWithContacts = useCallback(() => {
    if (!loading) {
      const isFormValid = onValidateContacts();

      if (!isFormValid) {
        setWarning(t('bookingsServices:addBookingServiceContactsWarning'));
        return;
      } else {
        setWarning('');
      }

      proceed(
        contactsForms.map((contact) => ({
          booking_contact_id: contact.booking_contact_id,
          calendar_ids: contact.calendar_ids,
        })),
      );
    }
  }, [loading, onValidateContacts, proceed, contactsForms, setWarning, t]);

  useEffect(() => {
    if (shouldValidate) {
      onProceedWithContacts();
      setShouldValidate(false);
    }
  }, [onProceedWithContacts, shouldValidate, setShouldValidate]);

  return (
    <Stack spacing={2}>
      {warning && <Alert severity='warning'>{warning}</Alert>}
      <AddContactFormList
        contactsListErrors={formErrors}
        getContacts={(contacts) => {
          setContactsForms(contacts);
        }}
      />
    </Stack>
  );
};

export default ContactsDetails;
